import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, SquarePlus, Zap } from "lucide-react";
import {
  Sheet, SheetContent, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

// Slick dismissible install banner that floats just above the bottom tab bar.
export function InstallBanner({ show, onInstall, onDismiss }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="install-banner"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 36 }}
          className="md:hidden fixed bottom-[4.75rem] inset-x-3 z-40 flex items-center gap-3 rounded-2xl bg-foreground text-background shadow-2xl px-3.5 py-3"
        >
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shrink-0">
            <Zap className="h-5 w-5 fill-current" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight">Install AI Pathshaalaa</p>
            <p className="text-xs text-background/70 truncate">Full-screen app — one tap away</p>
          </div>
          <button data-testid="install-banner-cta" onClick={onInstall}
            className="tap shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm px-4 py-2 active:scale-95">
            <Download className="h-4 w-4" /> Install
          </button>
          <button data-testid="install-banner-dismiss" onClick={onDismiss} aria-label="Dismiss"
            className="tap shrink-0 grid place-items-center h-8 w-8 rounded-full text-background/60 active:bg-background/10">
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// iOS Safari has no install prompt API — show the Share → Add to Home Screen steps.
export function IosInstallSheet({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="ios-install-sheet"
        className="md:hidden rounded-t-3xl border-t border-border/60 px-5 pt-3 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div className="mx-auto h-1.5 w-12 rounded-full bg-border mb-4" />
        <SheetTitle className="font-heading text-xl">Add to Home Screen</SheetTitle>
        <SheetDescription className="text-muted-foreground">
          Install AI Pathshaalaa on your iPhone in two taps — it opens full-screen, just like a native app.
        </SheetDescription>
        <ol className="mt-5 space-y-3.5">
          <li className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-primary/10 text-primary font-bold shrink-0">1</span>
            <span className="flex items-center gap-1.5 text-sm flex-wrap">
              Tap the <Share className="h-4 w-4 text-primary inline" /> <b>Share</b> button in Safari's toolbar
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-primary/10 text-primary font-bold shrink-0">2</span>
            <span className="flex items-center gap-1.5 text-sm flex-wrap">
              Scroll and choose <SquarePlus className="h-4 w-4 text-primary inline" /> <b>Add to Home Screen</b>
            </span>
          </li>
        </ol>
        <button onClick={() => onOpenChange(false)} data-testid="ios-install-done"
          className="tap mt-6 w-full rounded-2xl bg-primary text-primary-foreground font-semibold py-3.5 active:scale-[0.98]">
          Got it
        </button>
      </SheetContent>
    </Sheet>
  );
}
